
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action, State } from "vuex-class"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import SureViewIcon from "@/components/SureViewIcon.vue"

import { IntegrationLog, IntegrationService, IntegrationStatus, IntegrationSummary, MessageType, ServiceStatus } from "@/types/integration"

import integrationApi from "@/services/api_Integrations"
import { TenantFeatures } from "@/types/tenantFeatures"

/**
 * IntegrationView Component
 * Manages integrations and displays their status and logs.
 */
@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"sv-icon": SureViewIcon
	}
})
export default class IntegrationView extends Vue {
	@Prop()
	integration!: IntegrationSummary // Current integration being displayed

	@Getter("getIsFeatureEnabled") getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	tenantFeatures = TenantFeatures

	// --- Data ---
	// The maximum characters to display for a log message before ellipsis
	LogMessageDisplayCharLimit = 300

	logColumns = [
		{
			key: "occurred"
		},
		{
			key: "environmentName",
			label: "Environment"
		},
		{
			key: "messageType"
		},
		{
			key: "message"
		},
		{
			key: "caseNumber",
			label: "Record"
		}
	]

	services: IntegrationService[] = []
	logMessages: IntegrationLog[] = []

	logsOldestDate: Date | null = null
	logsMoreAvailable: boolean = false
	loadingLogs: boolean = false

	status: IntegrationStatus | null = null
	loadingStatus: boolean = false

	forceStateWait: NodeJS.Timeout | null = null

	// --- Lifecycle Hooks ---

	async mounted () {
		if (this.integration) {
			this.refresh()
		}
	}

	async beforeDestroy () {
		// Teardown state refresher
		if (this.forceStateWait) {
			clearInterval(this.forceStateWait)
			this.forceStateWait = null
		}
	}

	// --- Event Handlers ---

	/**
	 * force the integration to be queued for running
	 */
	public async forceRun () {
		await integrationApi.forceRun(this.integration.integrationKey)
		await this.statusGet(true)
	}

	/**
	 * Loads integration logs and updates the logMessages array.
	 */
	public async loadLogs () {
		this.loadingLogs = true
		const logs = await integrationApi.logGet(this.integration.integrationKey, this.logsOldestDate)
		this.logMessages.push(...logs)

		if (logs.length > 0) {
			this.logsOldestDate = logs[logs.length - 1].occurred
		}
		this.logsMoreAvailable = logs.length === 100
		this.loadingLogs = false
	}

	/**
	 * Opens the specified record in a new tab or navigates to it.
	 * @param {string} caseNumber - The case number of the record to open.
	 * @param {boolean} newTab - Indicates whether to open the record in a new tab.
	 */
	public openRecord (caseNumber: string, newTab: boolean = false) {
		const encodedCaseNumber = encodeURIComponent(caseNumber)
		if (newTab) {
			const routeData = this.$router.resolve({ path: `/cases/${encodedCaseNumber}` })
			window.open(routeData.href, "_blank")
		} else {
			this.$router.push({ path: `/cases/${encodedCaseNumber}` })
		}
	}

	/**
	 * Refreshes status and logs. Log messages will be reset from the start
	 */
	public refresh () {
		integrationApi.integrationServicesGet(this.integration.integrationKey)
			.then(services => this.services = services)

		this.logMessages = []
		this.logsOldestDate = null

		this.loadLogs()

		if (this.integration.allowForceRun) {
			this.statusGet(true)
		}
	}

	// --- Utility Functions ---

	/**
	 * Returns the CSS class name based on the service status.
	 * @param {ServiceStatus | null} status - The status of the service.
	 * @returns {string} - The CSS class name associated with the status.
	 */
	serviceClass (status: ServiceStatus | null): string {
		if (status == null) {
			return "text-muted"
		}

		switch (status) {
			case ServiceStatus.Ok: return "text-success"
			case ServiceStatus.Warning: return "text-warning"
			case ServiceStatus.Error: return "text-danger"
			case ServiceStatus.Offline: return "text-muted"
		}
	}

	/**
	 * Returns the human-readable message type based on the message type enum.
	 * @param {MessageType} type - The message type enum value.
	 * @returns {string} - The human-readable message type.
	 */
	messageType (type: MessageType): string {
		switch (type) {
			case MessageType.Error: return "Error"
			case MessageType.Warning: return "Warning"
			case MessageType.Information: return "Information"
		}

		return type // Currently unreachable, but if additional MessageTypes are ever added, it will handle it
	}

	/**
	 * Returns the appropriate CSS class for message type text styling
	 * @param {MessageType} type - The message type
	 * @returns {string} - CSS class for styling
	 */
	private messageTypeClass (type: MessageType): string {
		switch (type) {
			case MessageType.Error:
				return "text-danger"
			case MessageType.Warning:
				return "text-warning"
			default:
				return ""
		}
	}

	/**
     * Fetches latest integration logs and adds to start of logMessage list
     */
	async loadNewLogs () {
		if (this.logMessages.length > 0) {
			const newestLog = this.logMessages[0].occurred
			const logs = await integrationApi.logGetNew(this.integration.integrationKey, newestLog, new Date())

			this.logMessages.unshift(...logs) // Insert at start
		} else {
			this.loadLogs()
		}
	}

	/**
     * Retrieves the integration status and updates the status property.
     * @param {boolean} waitForRun - Indicates whether to start an interval to continually check for run to have been done.
     */
	async statusGet (waitForRun: boolean = false) {
		this.loadingStatus = true

		try {
			this.status = await integrationApi.statusGet(this.integration.integrationKey)
		} finally {
			this.loadingStatus = false
		}

		if (waitForRun && this.status && !this.status.nextCheck && !this.forceStateWait) {
			this.waitForRun()
		}
	}

	/**
	 * Starts an interval to continually re-check for the integration to have run
	 */
	waitForRun () {
		if (this.forceStateWait) {
			clearInterval(this.forceStateWait)
			this.forceStateWait = null
		}

		this.forceStateWait = setInterval(async () => {
			this.status = await integrationApi.statusGet(this.integration.integrationKey)
			if (this.status && this.status.nextCheck && this.forceStateWait) {
				clearInterval(this.forceStateWait)
				this.forceStateWait = null

				this.loadNewLogs()
			}
		}, 1000)
	}

	get maxRefreshTimeString (): string {
		switch (this.integration.integrationKey) {
			case "crud":
			case "scheduleManagementSync":
				return "Within 1 minute"
			default:
				return "Now"
		}
	}
}

